import { Button, Modal } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

export interface ClashComparisonRow {
  label: string;
  before: string;
  after: string;
}

interface Props {
  isOpen: boolean;
  /** Código del 409 del server: define título y subtítulo del aviso. */
  code: 'FUTURE_VALID_FROM_TAKEN' | 'ACTIVE_CONFIG_TODAY' | null;
  /** Prefijo i18n de la sección dueña (collectionFees / lateInterest). */
  dictPrefix: string;
  rows: ClashComparisonRow[];
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Aviso server-side de choque de fecha (flujo PO): muestra el antes (la
 * configuración que ya existe para esa fecha) y el después (lo recién
 * llenado). Confirmar actualiza la prevista futura o sobrescribe la vigente
 * de hoy con un registro nuevo, según el código; cancelar cierra sólo este
 * aviso y deja el modal de creación abierto con los datos.
 */
export function DateClashConfirmModal({
  isOpen,
  code,
  dictPrefix,
  rows,
  loading,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const variant = code === 'ACTIVE_CONFIG_TODAY' ? 'today' : 'future';

  return (
    <Modal isOpen={isOpen} toggle={onCancel} size="md">
      <h1 className="text-dark fs-22 mb-2 text-center">
        {t(`${dictPrefix}.clash.${variant}.title`)}
      </h1>
      <p className="text-center">{t(`${dictPrefix}.clash.${variant}.subtitle`)}</p>
      <Row className="fw-700 border-bottom pb-1">
        <Col xs={4}>{t(`${dictPrefix}.clash.field`)}</Col>
        <Col xs={4}>{t(`${dictPrefix}.clash.before`)}</Col>
        <Col xs={4}>{t(`${dictPrefix}.clash.after`)}</Col>
      </Row>
      {rows.map((row) => (
        <Row key={row.label} className="py-1 border-bottom">
          <Col xs={4}>{row.label}</Col>
          <Col xs={4}>{row.before}</Col>
          <Col xs={4} className="fw-700">
            {row.after}
          </Col>
        </Row>
      ))}
      <Row className="mt-4">
        <Col xs={12} md={6}>
          <Button
            outlined
            fullwidth
            text={t(`${dictPrefix}.clash.cancel`)}
            onClick={onCancel}
            disabled={loading}
          />
        </Col>
        <Col xs={12} md={6}>
          <Button
            fullwidth
            loading={loading}
            text={t(`${dictPrefix}.clash.confirm`)}
            onClick={onConfirm}
          />
        </Col>
      </Row>
    </Modal>
  );
}
